import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Palette, Settings, FileText, Mic } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import Navbar from '../../components/layout/Navbar';

const guidelines = [
  {
    title: 'Setting Up Your Brand Kit',
    icon: Settings,
    description: 'Create a comprehensive brand kit for consistent content',
    content: [
      'Upload your primary logo in high resolution (PNG with transparency)',
      'Add secondary logos, wordmarks, and icon variations',
      'Include brand symbols, patterns, or graphic elements',
      'Set up logo usage rules and minimum size requirements',
      'Define clear and exclusion zones around your logo',
      'Upload different logo versions for light and dark backgrounds'
    ]
  },
  {
    title: 'Color Palette Management',
    icon: Palette,
    description: 'Define and maintain your brand colors across all content',
    content: [
      'Set primary brand colors (usually 2-3 main colors)',
      'Define secondary colors for accents and highlights',
      'Include neutral colors (grays, whites, blacks)',
      'Specify exact color codes (HEX, RGB, CMYK)',
      'Create color combinations and approved palettes',
      'Set rules for color usage in different contexts',
      'Test colors for accessibility and contrast ratios'
    ]
  },
  {
    title: 'Logo and Asset Guidelines',
    icon: FileText,
    description: 'Establish clear rules for using your brand assets',
    content: [
      'Define minimum logo sizes for different platforms',
      'Specify clear space requirements around logos',
      'List approved and prohibited logo modifications',
      'Set guidelines for logo placement on images',
      'Create rules for logo usage on different backgrounds',
      'Establish hierarchy for multiple brand elements',
      'Define when to use different logo variations'
    ]
  },
  {
    title: 'Brand Voice Consistency',
    icon: Mic,
    description: 'Maintain consistent messaging and tone across platforms',
    content: [
      'Define your brand personality (professional, friendly, bold, etc.)',
      'Create a tone of voice guide with examples',
      'Set guidelines for language and vocabulary choices',
      'Establish rules for emoji and hashtag usage',
      'Define your brand\'s stance on current events and trends',
      'Create templates for common message types',
      'Set guidelines for responding to comments and messages'
    ]
  }
];

const BrandGuidelines: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-green-50/30">
      <Navbar />
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-gray-200/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link to="/resources" className="inline-flex items-center text-green-600 hover:text-green-700 mb-6">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Resources
            </Link>
            <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Brand Guidelines
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl">
              Learn how to maintain consistent brand identity across all your social media content and build recognition.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="space-y-8">
          {guidelines.map((guideline, index) => {
            const Icon = guideline.icon;
            return (
              <motion.div
                key={guideline.title}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Card className="bg-white/70 backdrop-blur-sm border border-gray-200/60 shadow-lg">
                  <CardHeader>
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-teal-500 rounded-lg flex items-center justify-center">
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <CardTitle className="text-xl font-bold text-gray-900">
                          {guideline.title}
                        </CardTitle>
                        <CardDescription className="text-gray-600">
                          {guideline.description}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {guideline.content.map((item, itemIndex) => (
                        <li key={itemIndex} className="flex items-start space-x-3">
                          <Palette className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Brand Consistency Tips */}
        <motion.div
          className="mt-12"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <Card className="bg-gradient-to-r from-green-500 to-teal-500 text-white border-0">
            <CardHeader>
              <CardTitle className="text-2xl font-bold flex items-center">
                <Palette className="w-6 h-6 mr-3" />
                Brand Consistency Checklist
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-green-100">
                <li>✓ Logo appears consistently across all platforms</li>
                <li>✓ Colors match your brand palette exactly</li>
                <li>✓ Typography follows your brand guidelines</li>
                <li>✓ Tone of voice is consistent in all captions</li>
                <li>✓ Visual style aligns with brand personality</li>
                <li>✓ All content feels cohesive and recognizable</li>
              </ul>
            </CardContent>
          </Card>
        </motion.div>

        {/* CTA */}
        <motion.div
          className="mt-12 text-center"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <div className="bg-white/70 backdrop-blur-sm border border-gray-200/60 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Ready to Build Your Brand Kit?</h2>
            <p className="text-gray-600 mb-6">
              Set up your brand guidelines and start creating consistent, professional content.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/app/clients">
                <Button size="lg" className="bg-gradient-to-r from-green-500 to-teal-500 hover:from-green-600 hover:to-teal-600 text-white">
                  Set Up Brand Kit
                </Button>
              </Link>
              <Link to="/resources">
                <Button size="lg" variant="outline">
                  More Resources
                </Button>
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default BrandGuidelines;